import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { User } from 'src/features/auth/types';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useUpdateUser } from 'src/features/users/api/useUpdateUser';
import { userMessages } from 'src/features/users/utils/messages';
import { toast } from 'src/hooks/useToast';
import { ICON_MAP } from 'src/utils/constants';
import { LIST_USERS_RESOURCE } from 'src/features/users/api/useListUsers';

interface UserFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedUser: User;
    children?: React.ReactNode;
}

function DeactivateUserFormModal<TData>({ selectedUser, children }: UserFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const { mutate: updateUser, isPending: isPendingDeactivate } = useUpdateUser({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_USERS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(userMessages.userActivationSuccess),
                description: formatMessage(userMessages.userActivationSuccessDescription, {
                    entry: selectedUser.name,
                }),
            });
        },
    });

    const onSubmit = () => updateUser({ userId: selectedUser.id, data: { is_active: false } });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.COG} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage {...userMessages.deactivateUser} />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={userMessages.deactivateUserSubtitle.id}
                                    defaultMessage={userMessages.deactivateUserSubtitle.defaultMessage}
                                    values={{
                                        entry: selectedUser.name,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={userMessages.confirmDeactivateUser.id}
                            defaultMessage={userMessages.confirmDeactivateUser.defaultMessage}
                            values={{
                                entry: selectedUser.name,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button disabled={isPendingDeactivate} type="submit" onClick={onSubmit} className="flex-1">
                        {isPendingDeactivate && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage {...userMessages.deactivateUser} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeactivateUserFormModal;
